import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell } from "@/components/PageShell";
import { locations } from "@/lib/healic-data";
import { locationHref } from "@/lib/location-routes";
import { MapPin, Phone, ArrowRight } from "lucide-react";
import { contact } from "@/lib/healic-data";
import hospitalExterior from "@/assets/hospital-exterior.jpg";
import hospitalGurugram from "@/assets/hospital-gurugram.webp";
import hospitalGhaziabad from "@/assets/hospital-ghaziabad.webp";
import hospitalHaridwar from "@/assets/hospital-haridwar.webp";
import hospitalAgra from "@/assets/hospital-agra.webp";

const locationImages: Record<string, string> = {
  "sohna-road-gurugram": hospitalGurugram,
  "indirapuram-ghaziabad": hospitalGhaziabad,
  "haridwar": hospitalHaridwar,
  "agra": hospitalAgra,
};

export const Route = createFileRoute("/locations/")({
  head: () => ({
    meta: [
      { title: "Healic Hospitals — Gurugram, Ghaziabad, Haridwar, Agra & Muzaffarnagar" },
      { name: "description", content: "Find a Healic multispecialty hospital near you in Gurugram, Ghaziabad, Haridwar, Agra or Muzaffarnagar." },
      { property: "og:title", content: "Healic Multispeciality Hospital Locations" },
      { property: "og:description", content: "Healic hospitals across North India offering multispecialty care close to home." },
      { property: "og:url", content: "https://healic.in/locations" },
    ],
    links: [{ rel: "canonical", href: "https://healic.in/locations" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          itemListElement: locations.map((l, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
              "@type": "Hospital",
              name: `Healic Multispeciality Hospital — ${l.city}`,
              address: l.address,
              telephone: contact.phone,
              url: `https://healic.in${locationHref(l.slug)}`,
            },
          })),
        }),
      },
    ],
  }),
  component: LocationsPage,
});


function LocationsPage() {
  return (
    <PageShell>
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{ backgroundImage: `url(${hospitalExterior})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/95 to-primary/70" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-4 py-10 lg:px-8 lg:py-12">
          <p className="text-xs font-semibold uppercase tracking-widest text-accent">Locations</p>
          <h1 className="mt-2 font-display text-2xl font-bold sm:text-3xl">Find a Healic hospital near you</h1>
          <p className="mt-2 max-w-2xl text-sm opacity-85">
            Multispecialty hospitals with 7 Am to 12 Am emergency, ICU, NICU, OPD, IPD, pharmacy and diagnostics.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 lg:px-8">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-2">
          {locations.map((l) => (
            <article key={l.slug} className="overflow-hidden rounded-2xl border border-border bg-card transition hover:shadow-card">
              <div className="aspect-[16/9] overflow-hidden bg-primary-soft">
                <img
                  src={locationImages[l.slug] ?? hospitalExterior}
                  alt={`Healic Hospital ${l.city}`}
                  loading="lazy"
                  width={1920}
                  height={1080}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent-soft text-accent">
                    <MapPin className="h-4 w-4" />
                  </div>
                  <h2 className="font-display text-base font-semibold">Healic — {l.city}</h2>
                </div>
                <p className="mt-3 text-sm text-muted-foreground">{l.address}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Link
                    to={locationHref(l.slug)}
                    className="inline-flex items-center gap-2 rounded-pill bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90"
                  >
                    View details <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                  <a
                    href={`tel:${contact.phoneTel}`}
                    className="inline-flex items-center gap-2 rounded-pill bg-cream px-3 py-1.5 text-xs font-semibold"
                  >
                    <Phone className="h-3.5 w-3.5" /> Call
                  </a>
                  <a
                    href={`https://www.google.com/maps/search/${encodeURIComponent("Healic " + l.city)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-pill bg-cream px-3 py-1.5 text-xs font-semibold"
                  >
                    Directions
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>
    </PageShell>
  );
}
